import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FileText, RefreshCw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Search, ShieldCheck, ScrollText, X,
} from "lucide-react";
import { fetchAudit, getLogs, type LogEntry, type GetLogsParams } from "../services/api";
import type { AuditEvent, AuditEventType } from "../types";
import { Link, useSearchParams } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

type Tab = "user" | "system";

// ─── Audit (User activity) tab ───────────────────────────────────────────────

const AUDIT_TYPE_OPTIONS: AuditEventType[] = [
  "operator.login", "operator.logout",
  "tenant.created", "tenant.updated", "tenant.suspended", "tenant.unsuspended", "tenant.deleted",
  "tenant.plan_changed", "tenant.limits_overridden",
  "user.invited", "user.role_changed", "user.removed", "user.password_reset",
  "billing.marked_paid", "billing.trial_extended", "billing.comped",
  "impersonation.started", "impersonation.ended"
];

function UserTab() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [type, setType] = useState<AuditEventType | "all">("all");

  useEffect(() => {
    let active = true;
    fetchAudit()
      .then((e) => { if (active) setEvents(e); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const filtered = events.filter((e) => {
    if (type !== "all" && e.type !== type) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      e.summary.toLowerCase().includes(q) ||
      e.actorEmail.toLowerCase().includes(q) ||
      (e.tenantName ?? "").toLowerCase().includes(q) ||
      e.type.includes(q)
    );
  });

  return (
    <>
      <div className="card" style={{ padding: 16 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="search-bar" style={{ flex: "1 1 280px" }}>
            <Search size={14} />
            <input
              type="search"
              placeholder="Search by tenant, actor, or summary…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value as AuditEventType | "all")} style={{ maxWidth: 260 }}>
            <option value="all">All event types</option>
            {AUDIT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="empty-state"><p className="muted">Loading audit events…</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <ScrollText size={28} />
            <h3>No matching events</h3>
            <p>Adjust filters to see more.</p>
          </div>
        ) : (
          filtered.map((e) => (
            <div key={e.id} className="audit-entry">
              <div className="time">
                <div>{new Date(e.occurredAt).toLocaleDateString("en-ZA")}</div>
                <div>{new Date(e.occurredAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <div>
                <div className="who">{e.actorEmail}</div>
                <div className="what">
                  {e.summary}
                  {e.tenantId && e.tenantName && (
                    <> · <Link to={`/companies/${e.tenantId}`} className="target">{e.tenantName}</Link></>
                  )}
                </div>
                <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>{e.ip}</div>
              </div>
              <span className="pill accent">{e.type}</span>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// ─── Multi-select checkbox dropdown ──────────────────────────────────────────
// Dropdown is rendered via React Portal into document.body, placing it entirely
// outside the backdrop-filter ancestor so position:fixed is viewport-relative.

function MultiSelect({
  options, value, onChange, allLabel,
}: {
  options: string[]; value: string[]; onChange: (v: string[]) => void; allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !listRef.current?.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (opt: string) =>
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);

  const label =
    value.length === 0 ? allLabel :
      value.length === 1 ? value[0] :
        `${value.length} selected`;

  return (
    <div style={{ display: "inline-block" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "7px 10px",
          border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)",
          color: "var(--text)", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap",
          minWidth: 160, maxWidth: 220,
        }}
      >
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        <ChevronDown size={12} style={{ flexShrink: 0 }} />
      </button>

      {open && createPortal(
        <div
          ref={listRef}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            minWidth: pos.width,
            zIndex: 9999,
            background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 6,
            padding: "4px 0", width: "max-content", maxWidth: 300,
            boxShadow: "0 6px 24px rgba(0,0,0,.2)", overflowY: "auto",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", padding: "7px 12px", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={value.length === 0}
              onChange={() => onChange([])}
              style={{ margin: 0, flexShrink: 0, cursor: "pointer", width: 14, height: 14 }}
            />
            {allLabel}
          </label>
          <div style={{ borderTop: "1px solid var(--border)", margin: "2px 0" }} />
          {options.map((opt) => (
            <label key={opt} style={{ display: "flex", alignItems: "center", padding: "7px 12px", gap: 8, cursor: "pointer", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={value.includes(opt)}
                onChange={() => toggle(opt)}
                style={{ margin: 0, flexShrink: 0, cursor: "pointer", width: 14, height: 14 }}
              />
              {opt}
            </label>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Service logs (System) tab ────────────────────────────────────────────────

const SERVICES = [
  "orbit-api-gateway", "orbit-auth-service", "orbit-config-service",
  "orbit-crm-service", "orbit-analytics-service", "orbit-integration-service",
  "orbit-voice-service", "orbit-messaging-service", "orbit-ai-orchestrator",
];
const LEVELS = ["debug", "info", "warn", "error", "critical"];
const LEVEL_COLORS: Record<string, string> = {
  debug: "#6b7280", info: "#3b82f6", warn: "#f59e0b", error: "#ef4444", critical: "#7c2d12",
};
const PAGE_SIZE = 50;
const POLL_MS = 30_000;

// Returns a "YYYY-MM-DDTHH:mm" string in the user's local timezone — the format
// datetime-local inputs expect. toISOString() gives UTC; we need local.
function toLocalDt(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function SystemTab() {
  const [sp, setSp] = useSearchParams();

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filter state — initialized from URL; dates stored as local "YYYY-MM-DDTHH:mm"
  const [services, setServices] = useState<string[]>(() => sp.getAll("service"));
  const [levels, setLevels] = useState<string[]>(() => sp.getAll("level"));
  const [search, setSearch] = useState(() => sp.get("search") ?? "");
  const [committedSearch, setCommittedSearch] = useState(() => sp.get("search") ?? "");
  const [page, setPage] = useState(() => Math.max(0, parseInt(sp.get("page") ?? "0", 10)));
  const [start, setStart] = useState(() => sp.get("start") ?? "");
  const [end, setEnd] = useState(() => sp.get("end") ?? "");

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pageRef = useRef(page);

  const hasExpanded = expandedId !== null;

  // Default to last hour in LOCAL time (not UTC)
  useEffect(() => {
    if (!start && !end) {
      const now = new Date();
      setStart(toLocalDt(new Date(now.getTime() - 60 * 60 * 1000)));
      setEnd(toLocalDt(now));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync filter state to URL
  useEffect(() => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("service"); services.forEach((s) => next.append("service", s));
      next.delete("level"); levels.forEach((l) => next.append("level", l));
      if (committedSearch) next.set("search", committedSearch); else next.delete("search");
      if (page > 0) next.set("page", String(page)); else next.delete("page");
      if (start) next.set("start", start); else next.delete("start");
      if (end) next.set("end", end); else next.delete("end");
      return next;
    }, { replace: true });
  }, [services, levels, committedSearch, page, start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  const doFetch = useCallback(async () => {
    if (!start || !end) return;
    setLoading(true);
    try {
      // Convert local datetime strings to UTC ISO for the backend timestamptz comparison
      const params: GetLogsParams = {
        services, levels, search: committedSearch || undefined,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        limit: PAGE_SIZE, offset: pageRef.current * PAGE_SIZE,
      };
      const res = await getLogs(params);
      setLogs(res.items);
      setTotal(res.total);
    } catch {
      // empty state handles it
    } finally {
      setLoading(false);
    }
  }, [services, levels, committedSearch, start, end]);

  // Re-fetch (and reset to page 0) whenever filters change
  useEffect(() => {
    pageRef.current = 0;
    setPage(0);
    setExpandedId(null);
    void doFetch();
  }, [doFetch]);

  // Polling — paused while a row is expanded
  useEffect(() => {
    if (hasExpanded || !start || !end) return;
    timerRef.current = setInterval(() => void doFetch(), POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [doFetch, hasExpanded, start, end]);

  const goToPage = (p: number) => {
    pageRef.current = p;
    setPage(p);
    void doFetch();
  };

  const handleSearch = (val: string) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setCommittedSearch(val), 300);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <>
      {/* Filter card — overflow:visible so the fixed-position dropdown isn't clipped */}
      <div className="card" style={{ padding: 16, overflow: "visible" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Row 1: search + multi-selects */}
          <div className="row wrap" style={{ gap: 12, alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--muted)", marginBottom: 4 }}>Search</label>
              <div className="search-bar">
                <Search size={14} />
                <input
                  type="search"
                  placeholder="Search messages…"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                {search && (
                  <button onClick={() => handleSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--muted)", marginBottom: 4 }}>Start</label>
              <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--muted)", marginBottom: 4 }}>End</label>
              <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            <button className="btn secondary" onClick={() => void doFetch()} disabled={loading} style={{ alignSelf: "flex-end" }}>
              <RefreshCw size={14} style={loading ? { animation: "spin 1s linear infinite" } : undefined} />
              Refresh
            </button>
          </div>

          {/* Row 2: date range + refresh */}
          <div className="row wrap" style={{ gap: 12, alignItems: "flex-end" }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--muted)", marginBottom: 4 }}>Service</label>
              <MultiSelect options={SERVICES} value={services} onChange={setServices} allLabel="All services" />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--muted)", marginBottom: 4 }}>Level</label>
              <MultiSelect options={LEVELS} value={levels} onChange={setLevels} allLabel="All levels" />
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading && logs.length === 0 ? (
          <div className="empty-state"><p className="muted">Loading logs…</p></div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <FileText size={28} />
            <h3>No logs found</h3>
            <p>Adjust filters or wire LOG_INGEST_URL in each service.</p>
          </div>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "var(--muted)", width: 180 }}>Timestamp</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "var(--muted)", width: 80 }}>Level</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "var(--muted)", width: 180 }}>Service</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "var(--muted)" }}>Message</th>
                  <th style={{ padding: "12px 16px", width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((entry) => {
                  const isExpanded = expandedId === entry.id;
                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        style={{ borderBottom: "1px solid var(--border)", cursor: "pointer", background: isExpanded ? "var(--surface-2)" : undefined }}
                        onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      >
                        <td style={{ padding: "12px 16px", fontSize: 12 }}>{new Date(entry.ts).toLocaleString()}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{
                            background: LEVEL_COLORS[entry.level] ?? "#666",
                            color: "#fff", borderRadius: 4, padding: "2px 6px",
                            fontSize: 11, fontWeight: 600, display: "inline-block",
                          }}>
                            {entry.level}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 12 }}>{entry.service}</td>
                        <td style={{ padding: "12px 16px", fontSize: 12 }}>{entry.message}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--surface-2)" }}>
                          <td colSpan={5} style={{ padding: 16 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16 }}>
                              {entry.tenantId && <div><div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>Tenant ID</div><div style={{ fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>{entry.tenantId}</div></div>}
                              {entry.userId && <div><div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>User ID</div><div style={{ fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>{entry.userId}</div></div>}
                              {entry.requestId && <div><div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>Request ID</div><div style={{ fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>{entry.requestId}</div></div>}
                            </div>
                            {entry.meta && Object.keys(entry.meta).length > 0 && (
                              <div style={{ marginTop: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 6 }}>Metadata</div>
                                <pre style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 4, padding: 12, fontSize: 11, fontFamily: "monospace", overflow: "auto", maxHeight: 300 }}>
                                  {JSON.stringify(entry.meta, null, 2)}
                                </pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, borderTop: "1px solid var(--border)" }}>
                <button className="btn secondary" onClick={() => goToPage(Math.max(0, page - 1))} disabled={page === 0 || loading}>
                  <ChevronLeft size={14} />
                </button>
                <span style={{ fontSize: 13, color: "var(--muted)", minWidth: 100, textAlign: "center" }}>
                  Page {page + 1} of {totalPages}
                </span>
                <button className="btn secondary" onClick={() => goToPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1 || loading}>
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ─── Combined page ────────────────────────────────────────────────────────────

export function LogsPage() {
  usePageTitle("Logs");
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (sp.get("tab") as Tab) ?? "user");

  const switchTab = (t: Tab) => {
    setTab(t);
    setSp((prev) => { const next = new URLSearchParams(prev); next.set("tab", t); return next; }, { replace: true });
  };

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: "8px 20px", border: "none",
    borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
    background: "none", cursor: "pointer",
    fontWeight: tab === t ? 600 : 400,
    color: tab === t ? "var(--accent)" : "var(--muted)",
    fontSize: 14,
  });

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Logs</h1>
        </div>
        {tab === "user" && (
          <div className="page-actions">
            <span className="pill success"><ShieldCheck size={11} /> All actions logged</span>
          </div>
        )}
      </header>

      <div style={{ display: "flex", borderBottom: "1px solid var(--border)", marginBottom: 20 }}>
        <button style={tabStyle("user")} onClick={() => switchTab("user")}>User activity</button>
        <button style={tabStyle("system")} onClick={() => switchTab("system")}>System</button>
      </div>

      {tab === "user" ? <UserTab /> : <SystemTab />}
    </div>
  );
}
